import { APGCovid19Constants } from './model/apg-newview-constants';
import { CovidSamples } from './model/covid-sample';
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse,  HttpParams } from '@angular/common/http';
import { environment } from "../../environments/environment";
import { BBResponseContainer } from '../carrier/response-container';
import { timeout, tap, map, catchError } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { AnimalBatch, WellType } from './model/animalBatch';
import { KitsDTO } from '../masterdata/models/kits-dto';
import { Comment } from './model/comment';
import { SplitBatchDTO } from './model/split-batch';
import { ArchivePlateListRequestDTO } from './model/archive-request-dto';
import { throwError } from 'rxjs/internal/observable/throwError';
import { ControlStatus,UnboxedSample } from './model/unboxing-sample';
import { AnimalBatchExportFileDTO } from './model/animalBatchExportFile';
import { ResP } from './model/result';

@Injectable()
export class APGTestingService {

  defaultTimeout = 600000;

  readonly PROCESSMMDMSERVICE = 'admin/mdm/result-process';

  readonly ANIMAL_SAMPLE_ORDER = 'animalSampleOrder';

  constructor(private http: HttpClient) { }

  getBatchesForLab(labId: string): Observable<BBResponseContainer> {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "animalBatch/list";
    let requestParam = new HttpParams().set('lab', labId);
    const options = url ? { params: requestParam } : {};
    return this.http.get<BBResponseContainer>(url, options).pipe(timeout(environment.defaultTimeout));
  }

  getBatchForLabAndBatchId(labId: string, batchId: string): Observable<BBResponseContainer> {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "animalBatch";
    let requestParam = new HttpParams().set('lab', labId).set('batchID', batchId);
    const options = url ? { params: requestParam } : {};
    return this.http.get<BBResponseContainer>(url, options).pipe(timeout(environment.defaultTimeout));
  }

  saveSampleData(batch: AnimalBatch, noOfPositivePCRControls: number, noOfNegativePCRControls): Observable<BBResponseContainer> {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "animalBatch?noOfPositivePCRControls=" + noOfPositivePCRControls + "&noOfNegativePCRControls=" + noOfNegativePCRControls;
    return this.http.post<BBResponseContainer>(url, batch).pipe(timeout(environment.defaultTimeout), tap(event => { }, error => {
      error.url = url;
    }));
  }

  filterBatches(selectedStates: string, selectedLabId: string): Observable<BBResponseContainer> {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "animalBatch/filter";
    let requestParam = new HttpParams().set('selectedStatusList', selectedStates).set('labId', selectedLabId);
    const options = url ? { params: requestParam } : {};
    return this.http.get<BBResponseContainer>(url, options).pipe(timeout(environment.defaultTimeout));
  }

  uploadExcelSheet(formData: FormData, clippingMachine: boolean): Observable<BBResponseContainer> {
    if (clippingMachine) {
      let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "animalBatch/upload-clipping-file";
      return this.http.post<any>(url, formData).pipe(timeout(this.defaultTimeout), tap(event => { }, error => {
        error.url = url;
      }));
    }
    else {
      let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "animalBatch/upload";
      return this.http.post<any>(url, formData).pipe(timeout(this.defaultTimeout), tap(event => { }, error => {
        error.url = url;
      }));
    }
  }

  getPlateViewData(batchId: string, labId: string): Observable<BBResponseContainer> {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "animalBatch/batchIdForPlateView";
    let requestParam = new HttpParams().set('batchId', batchId).set('labId', labId);
    const options = url ? { params: requestParam } : {};
    return this.http.get<BBResponseContainer>(url, options).pipe(timeout(environment.defaultTimeout));
  }

  getBatchWithSample(sampleId: string, labId: string): Observable<BBResponseContainer> {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "animalBatch/getBatchFromSampleId";
    let requestParam = new HttpParams().set('sampleId', sampleId).set('labId', labId);
    const options = url ? { params: requestParam } : {};
    return this.http.get<BBResponseContainer>(url, options).pipe(timeout(environment.defaultTimeout));
  }

  excel(excelData: any) {
    return this.http.get(environment.BB_APG_SAMPLE_PROCESSING_URL + 'animalBatch/downloadExcel?batchId=' + excelData, { observe: 'response', responseType: 'blob' }).pipe(
      map((res) => {
        let data = {
          fileContent: new Blob([res.body], { type: res.headers.get('content-Type') }),
          filename: res.headers.get('filename')
        }
        return data;
      }), catchError(error => Observable.throw(null))
    );
  }

  getMachinesFromMDM(labId: string): Observable<BBResponseContainer> {
    let url = environment.BB_MDMSERVICE_URL + 'admin/mdm/machine/extractorAndCyclerTypes/' + labId + '/' + true;
    return this.http.get<BBResponseContainer>(url).pipe(timeout(environment.defaultTimeout));
  }

  getKitsFromMDM(labID: string, swabRestriction: string, extractorKits: string, pcrKits: string): Observable<BBResponseContainer> {
    let url = environment.BB_MDMSERVICE_URL + 'admin/mdm/kits/kitTypes' + '/' + labID + '/' + swabRestriction;
    let requestParam = new HttpParams().set('extractorKits', extractorKits).set('pcrKits', pcrKits);
    const options = url ? { params: requestParam } : {};
    return this.http.get<BBResponseContainer>(url, options).pipe(timeout(environment.defaultTimeout));
  }

  updateKitInMDM(kitsDTO: KitsDTO): Observable<BBResponseContainer> {
    let url = environment.BB_MDMSERVICE_URL + 'mdm/kits/chemicalInfo';
    return this.http.put<any>(url, kitsDTO).pipe(timeout(this.defaultTimeout), tap(event => { }, error => {
      error.url = url;
    }));
  }


  getBatchWithSampleUnboxing(sampleId: string, labId: string): Observable<BBResponseContainer> {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "animalBatch/getBatchFromSampleIdForUnboxingStep";
    let requestParam = new HttpParams().set('sampleId', sampleId).set('labId', labId);
    const options = url ? { params: requestParam } : {};
    return this.http.get<BBResponseContainer>(url, options).pipe(timeout(environment.defaultTimeout));
  }

  getPCRWorkfile(batchID: string, labId: string, redoBatches: string[]): Observable<BBResponseContainer> {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "animalBatch/pcrworkfile";
    let requestParam = new HttpParams().set('batchID', batchID).set('labId', labId).set('redoBatches', redoBatches.join(','));
    const options = url ? { params: requestParam } : {};
    return this.http.get<BBResponseContainer>(url, options).pipe(timeout(environment.defaultTimeout));
  }

  validateEurofinsBarcode(scannedEurofinsBarcode: string, labId: string, isBarcodeValidationRequired: boolean): Observable<BBResponseContainer> {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "animalBatch/validate/barcode";
    let requestParam = new HttpParams().set('scannedBarcode', scannedEurofinsBarcode).set('labId', labId).set('isBarcodeExistanceCheckRequired', isBarcodeValidationRequired.toString());
    const options = url ? { params: requestParam } : {};
    return this.http.get<BBResponseContainer>(url, options).pipe(timeout(environment.defaultTimeout));
  }

  filterBatchesForSamplesMenu(selectedStates: string, selectedLabId: string, fromDate: Date, mergeGroup: string, workFlowNameList: string): Observable<BBResponseContainer> {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "animalBatch/filter";
    let filterDate = null;
    if (fromDate != null) {
      filterDate = fromDate.toDateString();
    }
    let requestParam = new HttpParams()
      .set('selectedStatusList', selectedStates)
      .set('labId', selectedLabId)
      .set('fromDate', filterDate)
      .set('mergeGroupList', mergeGroup)
      .set('workFlowNameList',workFlowNameList);
    const options = url ? { params: requestParam } : {};
    return this.http.get<BBResponseContainer>(url, options).pipe(timeout(environment.defaultTimeout));
  }

  uploadResultFile(formData: FormData, batchID: string, gene: string, dominantChannel: string, labId: string, rtPCRKitVendor: string, machineIOCapcity: string, fileType: string): Observable<BBResponseContainer> {

    let url = "";
    let requestParam = new HttpParams();
    if (!gene || gene.trim() == "") {
      url = environment.BB_APG_SAMPLE_PROCESSING_URL + "animalBatch/upload/result/" + labId + "/" + batchID;
    } else if (fileType == APGCovid19Constants.fastFinder) {
      requestParam = requestParam.set('fileType', fileType);
      url = environment.BB_APG_SAMPLE_PROCESSING_URL + "animalBatch/fastFinderUpload/result/" + labId + "/" + batchID;
    }
    else {
      requestParam = requestParam.set('gene', gene).set('dominantChannel', dominantChannel).set('rtPCRKitVendor', rtPCRKitVendor).set('machineIOCapcity', machineIOCapcity).set('fileType', fileType);
      url = environment.BB_APG_SAMPLE_PROCESSING_URL + "animalBatch/upload/result/" + labId + "/" + batchID;
    }
    const options = url ? { params: requestParam } : {};
    return this.http.post<BBResponseContainer>(url, formData, options).pipe(timeout(this.defaultTimeout), tap(event => { }, error => {
      error.url = url;
    }));
  }
  updateSamples(batchID: string, sampleToUpdate: CovidSamples, labId: string, wellType: string, sender: string, rushSample: boolean): Observable<BBResponseContainer> {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + 'animalBatch/sample/' + labId + '/' + batchID + '/' + wellType + '/' + sender + '/' + rushSample;
    return this.http.put<BBResponseContainer>(url, sampleToUpdate).pipe(timeout(environment.defaultTimeout));
  }

  confirmPlates(selectedBatch: AnimalBatch, reportingOutSideBB: boolean, reportPerSender: boolean): Observable<BBResponseContainer> {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "animalBatch/generate/csv/" + reportingOutSideBB + "?reportPerSender=" + reportPerSender;
    return this.http.post<BBResponseContainer>(url, selectedBatch).pipe(timeout(environment.defaultTimeout), tap(event => { }, error => {
      error.url = url;
    }));
  }

  downloadResultFile(batchId: string, labId: string) {
    return this.http.get(environment.BB_APG_SAMPLE_PROCESSING_URL + 'animalBatch/result/download/' + labId + '?batchId=' + batchId, { observe: 'response', responseType: 'blob' }).pipe(
      map((res) => {
        let data = {
          fileContent: new Blob([res.body], { type: res.headers.get('content-Type') }),
          filename: res.headers.get('filename')
        }
        return data;
      }), catchError(error => Observable.throw(null))
    );
  }

  validateScannedBarcode(barcode: string, labId: string): Observable<BBResponseContainer> {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "animalBatch/validate/" + barcode;
    let requestParam = new HttpParams().set('labId', labId);
    const options = url ? { params: requestParam } : {};
    return this.http.get<BBResponseContainer>(url, options).pipe(timeout(environment.defaultTimeout));
  }

  saveScannedSamples(selectedBatch: AnimalBatch): Observable<BBResponseContainer> {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "animalBatch/samples?batchID=" + selectedBatch.batchID;
    return this.http.patch<BBResponseContainer>(url, selectedBatch).pipe(timeout(this.defaultTimeout), tap(event => { }, error => {
      error.url = url;
    }));
  }

  saveContaminationInformation(batchID: string, sampleToUpdate: CovidSamples, labId: string): Observable<BBResponseContainer> {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "animalBatch/sample/contaminationInfo/" + batchID + "/" + labId;
    return this.http.patch<BBResponseContainer>(url, sampleToUpdate).pipe(timeout(this.defaultTimeout), tap(event => { }, error => {
      error.url = url;
    }));
  }

  filledExcel(data: string, labId: string) {
    let urlString = environment.BB_APG_SAMPLE_PROCESSING_URL + 'animalBatch/downloadFilledExcel?batchId=' + data + '&labId=' + labId;
    return this.http.get(urlString, { observe: 'response', responseType: 'blob' }).pipe(
      map((res) => {
        let response = {
          fileContent: new Blob([res.body], { type: res.headers.get('content-Type') }),
          filename: res.headers.get('filename')
        }
        return response;
      }), catchError(error => Observable.throw(null))
    );
  }

  undoMerge(batch: AnimalBatch): Observable<BBResponseContainer> {

    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "animalBatch/undoMerge" + "/" + batch.lab;

    return this.http.patch<BBResponseContainer>(url, batch).pipe(timeout(environment.defaultTimeout), tap(event => { }, error => {
      error.url = url;
    }));
  }


  getStorageForBatch(labId: string, batchId: string): Observable<BBResponseContainer> {
    let url = environment.BB_STORAGE_URL + 'storage/getBatchStorageLocation/' + batchId + '/' + labId;
    let requestParam = new HttpParams().set('lab', labId).set('batchID', batchId);
    const options = url ? { params: requestParam } : {};
    return this.http.get<BBResponseContainer>(url, options).pipe(timeout(environment.defaultTimeout));
  }

  saveComment(batchID: string, labId: string, comments: Comment[]): Observable<BBResponseContainer> {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "animalBatch/comment/" + batchID + "/" + labId;
    return this.http.patch<BBResponseContainer>(url, comments).pipe(timeout(environment.defaultTimeout));
  }

  resultReportExcelDowload(batchID: string, labId: string) {
    let urlString = environment.BB_APG_SAMPLE_PROCESSING_URL + 'animalBatch/export/report/' + labId + '/' + batchID;
    return this.http.get(urlString, { observe: 'response', responseType: 'blob' }).pipe(
      map((res) => {
        let response = {
          fileContent: new Blob([res.body], { type: res.headers.get('content-Type') }),
          filename: res.headers.get('filename')
        }
        return response;
      }), catchError(error => Observable.throw(null))
    );
  }

  getPCRWorkfileForBiorad(batchID: string, labId: string, redoBatches: string[], boxIdRequired: boolean) {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + 'animalBatch/specialpcrworkfile/biorad?batchID=' + batchID + '&labId=' + labId + '&redoBatches=' + redoBatches + '&boxIdRequired=' + boxIdRequired;
    return this.http.get(url, { observe: 'response', responseType: 'blob' }).pipe(
      map((res) => {
        let data = {
          fileContent: new Blob([res.body], { type: res.headers.get('content-Type') }),
          filename: res.headers.get('filename')
        }
        return data;
      }), catchError(error => Observable.throw(null))
    );
  }
  externalFileTransfer(selectedBatch: AnimalBatch): Observable<BBResponseContainer> {

    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "animalBatch/external/upload";
    return this.http.post<BBResponseContainer>(url, selectedBatch).pipe(timeout(this.defaultTimeout), tap(event => { }, error => {
      error.url = url;
    }));

  }

  getProcessList(labId: string, mergeGroup: string): Observable<BBResponseContainer> {
    let url = environment.BB_MDMSERVICE_URL + this.PROCESSMMDMSERVICE + "/" + labId + "/" + mergeGroup;
    return this.http.get<BBResponseContainer>(url).pipe(tap(event => { }, error => {
      error.url = url;
    }));
  }

  getWorkfileForBioRadLims(batchID: string, labId: string, redoBatches: string[], kit: any) {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + 'animalBatch/specialpcrworkfile/bioradlims?batchID=' + batchID + '&labId=' + labId + '&redoBatches=' + redoBatches;
    return this.http.put(url, kit, { observe: 'response', responseType: 'blob' }).pipe(
      map((res) => {
        let data = {
          fileContent: new Blob([res.body], { type: res.headers.get('content-Type') }),
          filename: res.headers.get('filename')
        }
        return data;
      }), catchError(error => Observable.throw(null))
    );
  }

  getWorkfileForFLA_ABI(batchID: string, labId: string, redoBatches: string[], quadList: string[]) {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "animalBatch/specialpcrworkfile/flaabi";

    let requestParam = new HttpParams().set('batchID', batchID).set('labId', labId).set('redoBatches', redoBatches.join(',')).set('quadrantList', quadList.join(','));
    const options = url ? { params: requestParam } : {};
    return this.http.get<BBResponseContainer>(url, options).pipe(timeout(environment.defaultTimeout));
  }

  getContactIdForBarcode(eurofinsBarcode: string) {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "animalBatch/shippedBarcode/contactId";
    let requestParam = new HttpParams().set('eurofinsBarcode', eurofinsBarcode);
    const options = url ? { params: requestParam } : {};
    return this.http.get<BBResponseContainer>(url, options).pipe(timeout(environment.defaultTimeout));
  }


  splitBatch(batchID: string, labId: string, splitBatchDTO: SplitBatchDTO): Observable<BBResponseContainer> {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "animalBatch/splitbatch/" + batchID + "/" + labId;
    return this.http.patch<BBResponseContainer>(url, splitBatchDTO).pipe(timeout(environment.defaultTimeout), tap(event => { }, error => {
      error.url = url;
    }));
  }
  getResultConfigBykitCycler(kitLot: string, machineName1: string, lab: string) {
    let url = environment.BB_MDMSERVICE_URL + "resultConfig/getByKitCycler/" + lab;
    let requestParam = new HttpParams().set('kit', kitLot).set('cycler', machineName1);
    const options = url ? { params: requestParam } : {};
    return this.http.get<any>(url, options).pipe(timeout(environment.defaultTimeout));
  }

  getSampleResultHistory(batchId: string, labId: string): Observable<BBResponseContainer> {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + 'animalBatch/sampleResultHistory';
    let requestParam = new HttpParams().set('batchId', batchId).set('labId', labId)
    const options = url ? { params: requestParam } : {};
    return this.http.get<BBResponseContainer>(url, options).pipe(timeout(environment.defaultTimeout), tap(event => { }, error => {
      error.url = url;
    }));
  }
  downloadRedoFiles(selectedLabId: string) {
    return this.http.get(environment.BB_APG_SAMPLE_PROCESSING_URL + 'animalBatch/singleSampleRedo/excel/' + selectedLabId, { observe: 'response', responseType: 'blob' }).pipe(map((res) => {
      let data = {
        fileContent: new Blob([res.body], { type: res.headers.get('content-Type') }),
        filename: res.headers.get('filename')
      }
      return data;
    }), catchError(error => Observable.throw(null))
    );
  }
  getRedoSamplesforLabSendors(selectedLabId: string) {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "animalBatch/singleSampleRedo/" + selectedLabId;
    return this.http.get<any>(url).pipe(timeout(environment.defaultTimeout));
  }

  getUploadedFileCountForCovidBatch(lab: string, batchID: string) {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "animalBatch/filesPerManufacturer/" + lab + "/" + batchID;
    return this.http.get<any>(url).pipe(timeout(environment.defaultTimeout));
  }

  getBatchHistory(lab: string, batchID: string, wellType: WellType, sourceBatchIds: string) {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "animalBatch/history/" + lab + "/" + batchID + "/" + wellType;
    let requestParam = new HttpParams().set('sourceBatchIds', sourceBatchIds)
    const options = url ? { params: requestParam } : {};
    return this.http.get<any>(url, options).pipe(timeout(environment.defaultTimeout));
  }

  getWorkFile(batchId: string, labId: string) {
    let urlString = environment.BB_APG_SAMPLE_PROCESSING_URL + "covid19ArchiveBatch/workFile/" + batchId + "/" + labId;
    return this.http.get(urlString, { observe: 'response', responseType: 'blob' }).pipe(
      map((res) => {
        let response = {
          fileContent: new Blob([res.body], { type: res.headers.get('content-Type') }),
          filename: res.headers.get('filename')
        }
        return response;
      }), catchError(error => Observable.throw(null))
    );
  }

  getFlowbotFile(batchId: string, labId: string) {
    let urlString = environment.BB_APG_SAMPLE_PROCESSING_URL + "covid19ArchiveBatch/workFileforFlowbot/" + batchId + "/" + labId;
    return this.http.get(urlString, { observe: 'response', responseType: 'blob' }).pipe(
      map((res) => {
        return this.fileResponse(res);
      }), catchError(this.parseErrorBlob)
    );
  }
  parseErrorBlob(err: HttpErrorResponse): Observable<any> {
    const reader: FileReader = new FileReader();
    const obs = new Observable((observer: any) => {
      reader.onloadend = (e) => {
        observer.error(JSON.parse((<any>e.target).result));
        observer.complete();
      }
    });
    reader.readAsText(err.error);
    return obs;
  }
  getStorageLocations(labId: string, archiveBatchSourceIds: string[]): Observable<BBResponseContainer> {
    let url = environment.BB_STORAGE_URL + "storage/getStorageLocation/" + labId;
    let requestParam = new HttpParams().set('batchId', archiveBatchSourceIds.join(','));
    const options = url ? { params: requestParam } : {};
    return this.http.get<BBResponseContainer>(url, options).pipe(timeout(this.defaultTimeout), tap(event => { }, error => {
      error.url = url;
    }));

  }

  getPlateList(batchId: string, labId: string, archiveRequestDTO: ArchivePlateListRequestDTO) {
    let urlString = environment.BB_APG_SAMPLE_PROCESSING_URL + "covid19ArchiveBatch/plateList/" + batchId + "/" + labId;
    return this.http.post(urlString, archiveRequestDTO, { observe: 'response', responseType: 'blob' }).pipe(
      map((res) => {
        let response = {
          fileContent: new Blob([res.body], { type: res.headers.get('content-Type') }),
          filename: res.headers.get('filename')
        }
        return response;
      }), catchError(error => Observable.throw(null))
    );
  }

  getUnclippedSamples(lab: string, unboxedSampleBarcodes: Array<string>): Observable<any> {
    const url = environment.BB_APG_SAMPLE_PROCESSING_URL + "animalBatch/unclipped/" + lab;
    return this.http.post<any>(url, unboxedSampleBarcodes).pipe(timeout(environment.defaultTimeout));
  }

  getPCRWorkfileForAreaDx(batchID: string, labId: string, redoBatches: string[]) {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + 'animalBatch/specialpcrworkfile/areadx?batchID=' + batchID + '&labId=' + labId + '&redoBatches=' + redoBatches;
    return this.http.get(url, { observe: 'response', responseType: 'blob' }).pipe(
      map((res) => {
        let data = {
          fileContent: new Blob([res.body], { type: res.headers.get('content-Type') }),
          filename: res.headers.get('filename')
        }
        return data;
      }), catchError(error => Observable.throw(null))
    );
  }

  getPCRWorkfileForAriaDx(batchID: string, labId: string, redoBatches: string[], pcrKitMap: any): Observable<BBResponseContainer> {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + 'animalBatch/specialpcrworkfile/ariadx?batchID=' + batchID + '&labId=' + labId + '&redoBatches=' + redoBatches;
    return this.http.post<BBResponseContainer>(url, pcrKitMap).pipe(timeout(environment.defaultTimeout));
  }

  getAriaDxXlsWorkfile(batchID: string, labId: string): Observable<any> {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + 'animalBatch/specialpcrworkfile/ariadx/exceldownload?batchID=' + batchID + '&labId=' + labId;
    return this.http.post(url, null, { observe: 'response', responseType: 'blob' }).pipe(
      map((res) => {
        return this.fileResponse(res);
      }), catchError(this.parseErrorBlob)
    );
  }
  fileResponse(res) {
    return {
      fileContent: new Blob([res.body], { type: res.headers.get('content-Type') })
    }
  }
  getPCRWorkfileForQuantStudio5(batchID: string, labId: string, redoBatches: string[], pcrKitMap: any): Observable<BBResponseContainer> {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + 'animalBatch/specialpcrworkfile/quantstudio5?batchID=' + batchID + '&labId=' + labId + '&redoBatches=' + redoBatches;
    return this.http.post<BBResponseContainer>(url, pcrKitMap).pipe(timeout(environment.defaultTimeout));
  }

  createReAnalysisbatches(rtBatch: AnimalBatch, batchesToReAnalyse: string[]): Observable<BBResponseContainer> {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + 'animalBatch/reanalysis/create';
    let requestParam = new HttpParams().set('batchesToReanalyse', batchesToReAnalyse.join(','));
    const options = url ? { params: requestParam } : {};
    return this.http.put<BBResponseContainer>(url, rtBatch, options).pipe(timeout(this.defaultTimeout), tap(event => { }, error => {
      error.url = url;
    }));
  }
  getRedoSample(lab: string, barcode: string): Observable<BBResponseContainer> {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + 'animalBatch/redosample/barcode?lab=' + lab + "&barcode=" + barcode;
    return this.http.get<BBResponseContainer>(url).pipe(tap(event => { }, error => {
      error.url = url;
    }));
  }
  uploadSamples(labId: string, formData: FormData): Observable<BBResponseContainer> {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "animalBatch/upload/sampleListUpload/" + labId;
    return this.http.post<any>(url, formData).pipe(timeout(this.defaultTimeout), tap(event => { }, error => {
      error.url = url;
    }));
  }

  archiveBatchFilledExcel(data: string, labId: string) {
    let urlString = environment.BB_APG_SAMPLE_PROCESSING_URL + 'animalBatch/archivedBatchFilledExcel?batchId=' + data + '&labId=' + labId;
    return this.getExcel(urlString);
  }

  uploadCSV(formData: FormData): Observable<BBResponseContainer> {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "animalBatch/upload-csv-sample-file";
    return this.http.post<any>(url, formData).pipe(timeout(this.defaultTimeout), tap(event => { }, error => {
      error.url = url;
    }));
  }

  private getExcel(urlString: string) {
    return this.http.get(urlString, { observe: 'response', responseType: 'blob' }).pipe(
      map((res) => {
        return {
          fileContent: new Blob([res.body], { type: res.headers.get('content-Type') }),
          filename: res.headers.get('filename')
        };
      }), catchError(error => throwError(null))
    );
  }

  undoArchive(batchID: string, labId: string, samples: string[]): Observable<BBResponseContainer> {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + 'covid19ArchiveBatch/undoArchive?batchID=' + batchID + '&labId=' + labId + '&samples=' + samples;
    return this.http.put<BBResponseContainer>(url, batchID).pipe(timeout(environment.defaultTimeout));
  }

  getSampleOrder(barcode: string, labId: string): Observable<BBResponseContainer> {
    let url = `${environment.BB_APG_SAMPLE_PROCESSING_URL}${this.ANIMAL_SAMPLE_ORDER}/order`;
    let requestParam = new HttpParams().set('labId', labId).set('barcode', barcode);
    const options = url ? { params: requestParam } : {};
    return this.http.get<BBResponseContainer>(url, options).pipe(timeout(environment.defaultTimeout));
  }

  getSampleOrderDetails(barcode: string, labId: string, delivery: string): Observable<BBResponseContainer> {
    let url = `${environment.BB_APG_SAMPLE_PROCESSING_URL}${this.ANIMAL_SAMPLE_ORDER}`;
    let requestParam = new HttpParams().set('labId', labId).set('barcode', barcode).set('delivery', delivery);
    const options = url ? { params: requestParam } : {};
    return this.http.get<BBResponseContainer>(url, options).pipe(timeout(environment.defaultTimeout));
  }

  updateUnboxingSampleStatus(batchId: string, labId: string, unboxingSample: UnboxedSample): Observable<BBResponseContainer> {
    let url = `${environment.BB_APG_SAMPLE_PROCESSING_URL}animalBatch/sample?labId=${labId}&batchId=${batchId}`;
    return this.http.put<BBResponseContainer>(url, unboxingSample).pipe(timeout(environment.defaultTimeout));
  }

  exportStrFile(animalBatchExportFile: AnimalBatchExportFileDTO){
    let url = `${environment.BB_APG_SAMPLE_PROCESSING_URL}animalBatch/export/str`;
    return this.http.post(url, animalBatchExportFile, { observe: 'response', responseType: 'blob' }).pipe(timeout(environment.defaultTimeout));
  }

  exportClippingFile(batchID: string, lab: string){
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL+'animalBatch/export/extraction?batchId='+batchID+'&labId='+lab;
    return this.http.get(url, { observe: 'response', responseType: 'blob' }).pipe(timeout(environment.defaultTimeout));
  }

  uploadStrResultFile(formData: FormData, batchID: string, lab: string): Observable<BBResponseContainer>{
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL+'animalBatch/result/upload?batchID='+batchID+'&lab='+lab;
    return this.http.post<BBResponseContainer>(url, formData).pipe(timeout(this.defaultTimeout));
  }

  filterWorkflowBatches(selectedStates: string, selectedLabId: string,workFlowNameList: string): Observable<BBResponseContainer> {
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + "animalBatch/filter";
    let requestParam = new HttpParams().set('selectedStatusList', selectedStates).set('labId', selectedLabId).set('workFlowNameList', workFlowNameList);
    const options = url ? { params: requestParam } : {};
    return this.http.get<BBResponseContainer>(url, options).pipe(timeout(environment.defaultTimeout));
  }
  getSampleSearch(labId:string){
    let url = environment.BB_APG_SAMPLE_PROCESSING_URL + 'animalBatch/sampleSearch?labId='+labId;
    return this.http.get<BBResponseContainer>(url).pipe(timeout(this.defaultTimeout));
  }
  getSearchedSample(sampleId:string, labId:string,){
    let url=environment.BB_APG_SAMPLE_PROCESSING_URL + 'animalBatch/samplesMarkers?labId='+labId+'&sampleId='+sampleId;
    return this.http.get<BBResponseContainer>(url).pipe(timeout(this.defaultTimeout));
  }
  updatingResultInStrP(resP:ResP,labId:string,sampleId:string){
    let url=environment.BB_APG_SAMPLE_PROCESSING_URL +'animalBatch/sample/result/strp?labId='+labId+'&sampleId='+sampleId;
    return this.http.post<BBResponseContainer>(url,resP).pipe(timeout(this.defaultTimeout));
  }
}
